/**
 * File: readDeviceFactsUtils.ts
 * Path: apps/admin/src/features/diagnostics/utils/readDeviceFactsUtils.ts
 * Description: Asks this browser the questions deviceChecks judges. Each
 * question that can fail answers "unknown" instead of breaking the page.
 */

import type { PushDevice } from "@idk/config";
import { readBrowserFacts } from "@/shared/utils/deviceUtils";
import type { DeviceFacts } from "./deviceChecksUtils";

/** Set at build time by vite.config.ts; missing on the dev server and in tests. */
declare const __ADMIN_BUILT_AT__: string | undefined;

export const adminBuiltAt = (): string | null =>
  typeof __ADMIN_BUILT_AT__ === "string" && __ADMIN_BUILT_AT__ ? __ADMIN_BUILT_AT__ : null;

export const readDeviceFacts = async (thisDevice: PushDevice | null): Promise<DeviceFacts> => {
  const browser = readBrowserFacts();
  let worker: DeviceFacts["worker"] = "none";
  let workerControlling = false;
  let subscribed: boolean | null = null;
  if (browser.hasServiceWorker) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      worker = registration?.active ? "active" : registration?.installing || registration?.waiting ? "installing" : "none";
      workerControlling = navigator.serviceWorker.controller !== null;
      if (registration && browser.hasPushManager) {
        subscribed = (await registration.pushManager.getSubscription()) !== null;
      }
    } catch {
      subscribed = null;
    }
  }
  return {
    ...browser,
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    worker,
    workerControlling,
    subscribed,
    thisDevice,
    builtAt: adminBuiltAt(),
  };
};
